import { Capacitor } from "@capacitor/core";
import { Keyboard } from "@capacitor/keyboard";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

import {
  computeCapacitorKeyboardInset,
  computeVisualViewportInset,
  type KeyboardInsetState,
} from "./keyboardInset";

function idleState(): KeyboardInsetState {
  return {
    bottomInset: 0,
    height: typeof window !== "undefined" ? window.innerHeight : 0,
  };
}

const KeyboardInsetContext = createContext<KeyboardInsetState>(idleState());

function setKeyboardInsetCssVariable(bottomInset: number) {
  document.documentElement.style.setProperty("--screen0-keyboard-inset", `${bottomInset}px`);
}

/** Tracks on-screen keyboard overlap for native iOS (Capacitor) and visualViewport elsewhere. */
export function KeyboardInsetProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<KeyboardInsetState>(idleState);

  useEffect(() => {
    const layoutHeight = () => window.innerHeight;

    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios") {
      let cancelled = false;
      const removeListeners: Array<() => void> = [];

      const applyKeyboardHeight = (keyboardHeight: number) => {
        if (cancelled) return;
        const next = computeCapacitorKeyboardInset(keyboardHeight, layoutHeight());
        setState(next);
        setKeyboardInsetCssVariable(next.bottomInset);
      };

      void (async () => {
        const handles = await Promise.all([
          Keyboard.addListener("keyboardWillShow", (info) => applyKeyboardHeight(info.keyboardHeight)),
          Keyboard.addListener("keyboardDidShow", (info) => applyKeyboardHeight(info.keyboardHeight)),
          Keyboard.addListener("keyboardWillHide", () => applyKeyboardHeight(0)),
          Keyboard.addListener("keyboardDidHide", () => applyKeyboardHeight(0)),
        ]);

        if (cancelled) {
          handles.forEach((handle) => handle.remove());
          return;
        }

        removeListeners.push(...handles.map((handle) => () => handle.remove()));
      })();

      return () => {
        cancelled = true;
        removeListeners.forEach((remove) => remove());
        document.documentElement.style.removeProperty("--screen0-keyboard-inset");
        setState(idleState());
      };
    }

    const viewport = window.visualViewport;
    if (!viewport) return;

    const update = () => {
      const next = computeVisualViewportInset(layoutHeight(), viewport.height, viewport.offsetTop);
      setState(next);
      setKeyboardInsetCssVariable(next.bottomInset);
    };

    update();
    viewport.addEventListener("resize", update);
    viewport.addEventListener("scroll", update);

    return () => {
      viewport.removeEventListener("resize", update);
      viewport.removeEventListener("scroll", update);
      document.documentElement.style.removeProperty("--screen0-keyboard-inset");
      setState(idleState());
    };
  }, []);

  return <KeyboardInsetContext.Provider value={state}>{children}</KeyboardInsetContext.Provider>;
}

export function useKeyboardInset(): KeyboardInsetState {
  return useContext(KeyboardInsetContext);
}

/** Applies keyboard inset only while the consuming surface is active (e.g. an open sheet). */
export function useKeyboardInsetWhen(enabled: boolean): KeyboardInsetState {
  const live = useKeyboardInset();
  const layoutHeight = typeof window !== "undefined" ? window.innerHeight : live.height;

  if (!enabled || live.bottomInset <= 0) {
    return { bottomInset: 0, height: layoutHeight };
  }

  return live;
}
